import React, { useState, useEffect } from 'react';
import datafile from "../information/Bio.json";
import MyCarousel from "../components/MyCarousel";
import "./BioEditorPro.css";
import Bio from "../components/pages/Bio"; // Create or merge into your existing CSS

const BioEditorPro = () => {
    // State for the entire bio data
    const [bioData, setBioData] = useState(datafile);

    // Keep the document title in sync
    useEffect(() => {
        document.title = bioData.title;
    }, [bioData.title]);

    /** --------------------------------------------------
     *   Basic Helpers for Nested Updates (No Lag)
     * -------------------------------------------------- */

        // Retrieve a nested value at a given path (array of keys/indices)
    const getNestedValue = (obj, path) => {
            return path.reduce((acc, key) => {
                if (acc == null) return acc;
                return acc[key];
            }, obj);
        };

    // Update a nested value at a given path
    const updateNestedValue = (obj, path, newValue) => {
        if (!path.length) return newValue;
        const [head, ...rest] = path;
        if (Array.isArray(obj)) {
            const copy = [...obj];
            copy[head] = updateNestedValue(copy[head], rest, newValue);
            return copy;
        } else if (typeof obj === "object" && obj !== null) {
            return {
                ...obj,
                [head]: updateNestedValue(obj[head], rest, newValue),
            };
        }
        // Fallback if something is unexpected
        return newValue;
    };

    // Delete a nested field or item
    const deleteNestedField = (obj, path) => {
        if (!path.length) return obj;
        const [head, ...rest] = path;
        if (Array.isArray(obj)) {
            const copy = [...obj];
            if (rest.length === 0) {
                // remove array item
                copy.splice(head, 1);
                return copy;
            } else {
                copy[head] = deleteNestedField(copy[head], rest);
                return copy;
            }
        } else if (typeof obj === "object" && obj !== null) {
            const copy = { ...obj };
            if (rest.length === 0) {
                // remove object key
                delete copy[head];
                return copy;
            } else {
                copy[head] = deleteNestedField(copy[head], rest);
                return copy;
            }
        }
        return obj;
    };

    /** ---------------------------------------------
     *    Top-Level Title & Carousel Handlers
     * --------------------------------------------- */
    const handleTitleChange = (e) => {
        setBioData((prev) => ({ ...prev, title: e.target.value }));
    };

    // Carousel
    const handleImageChange = (index, newUrl) => {
        setBioData((prev) => {
            const newImages = [...prev.carousel_images];
            newImages[index] = newUrl;
            return { ...prev, carousel_images: newImages };
        });
    };

    const handleAddImage = () => {
        const newImageUrl = prompt("Enter new image URL:");
        if (newImageUrl) {
            setBioData((prev) => ({
                ...prev,
                carousel_images: [...prev.carousel_images, newImageUrl.trim()],
            }));
        }
    };

    const handleDeleteImage = (index) => {
        setBioData((prev) => ({
            ...prev,
            carousel_images: prev.carousel_images.filter((_, i) => i !== index),
        }));
    };

    /** ---------------------------------------------
     *    Sections (Title, Reorder, Add/Delete)
     * --------------------------------------------- */
    const handleSectionTitleChange = (sectionIndex, newTitle) => {
        setBioData((prev) => {
            const sectionsCopy = [...prev.bio_sections];
            sectionsCopy[sectionIndex] = {
                ...sectionsCopy[sectionIndex],
                section_title: newTitle,
            };
            return { ...prev, bio_sections: sectionsCopy };
        });
    };

    const moveSectionUp = (index) => {
        if (index <= 0) return;
        setBioData((prev) => {
            const sectionsCopy = [...prev.bio_sections];
            [sectionsCopy[index - 1], sectionsCopy[index]] = [
                sectionsCopy[index],
                sectionsCopy[index - 1],
            ];
            return { ...prev, bio_sections: sectionsCopy };
        });
    };

    const moveSectionDown = (index) => {
        setBioData((prev) => {
            if (index >= prev.bio_sections.length - 1) return prev;
            const sectionsCopy = [...prev.bio_sections];
            [sectionsCopy[index], sectionsCopy[index + 1]] = [
                sectionsCopy[index + 1],
                sectionsCopy[index],
            ];
            return { ...prev, bio_sections: sectionsCopy };
        });
    };

    const handleDeleteSection = (sectionIndex) => {
        setBioData((prev) => ({
            ...prev,
            bio_sections: prev.bio_sections.filter((_, i) => i !== sectionIndex),
        }));
    };

    const handleAddSection = () => {
        const title = prompt("Enter new section title:");
        if (title) {
            const newSection = {
                section_title: title.trim(),
                content: [],
            };
            setBioData((prev) => ({
                ...prev,
                bio_sections: [...prev.bio_sections, newSection],
            }));
        }
    };

    /** ---------------------------------------------
     *    Reordering array items (Up/Down)
     * --------------------------------------------- */
    const moveArrayItemUp = (sectionIndex, path, itemIndex) => {
        if (itemIndex <= 0) return;
        setBioData((prev) => {
            const sectionsCopy = [...prev.bio_sections];
            const array = getNestedValue(sectionsCopy[sectionIndex].content, path);
            if (!Array.isArray(array)) return prev; // safety check

            const newArray = [...array];
            [newArray[itemIndex - 1], newArray[itemIndex]] = [
                newArray[itemIndex],
                newArray[itemIndex - 1],
            ];
            // Put reordered array back in
            sectionsCopy[sectionIndex].content = updateNestedValue(
                sectionsCopy[sectionIndex].content,
                path,
                newArray
            );
            return { ...prev, bio_sections: sectionsCopy };
        });
    };

    const moveArrayItemDown = (sectionIndex, path, itemIndex) => {
        setBioData((prev) => {
            const sectionsCopy = [...prev.bio_sections];
            const array = getNestedValue(sectionsCopy[sectionIndex].content, path);
            if (
                !Array.isArray(array) ||
                itemIndex >= array.length - 1
            ) {
                return prev;
            }

            const newArray = [...array];
            [newArray[itemIndex], newArray[itemIndex + 1]] = [
                newArray[itemIndex + 1],
                newArray[itemIndex],
            ];
            sectionsCopy[sectionIndex].content = updateNestedValue(
                sectionsCopy[sectionIndex].content,
                path,
                newArray
            );
            return { ...prev, bio_sections: sectionsCopy };
        });
    };

    /** ---------------------------------------------
     *    Recursively Render Editable Content
     * --------------------------------------------- */
    const renderEditableContent = (sectionIndex, content, path = []) => {
        // If string or number, treat as single input
        if (typeof content === "string" || typeof content === "number") {
            return (
                <div className="field-row" key={path.join("_")}>
                    <input
                        type="text"
                        value={content}
                        onChange={(e) => {
                            const newVal = e.target.value;
                            setBioData((prev) => {
                                const sectionsCopy = [...prev.bio_sections];
                                // Update nested field
                                sectionsCopy[sectionIndex].content = updateNestedValue(
                                    sectionsCopy[sectionIndex].content,
                                    path,
                                    newVal
                                );
                                return { ...prev, bio_sections: sectionsCopy };
                            });
                        }}
                        className="input-field"
                    />
                    <button
                        className="btn btn-danger"
                        onClick={() => {
                            setBioData((prev) => {
                                const sectionsCopy = [...prev.bio_sections];
                                sectionsCopy[sectionIndex].content = deleteNestedField(
                                    sectionsCopy[sectionIndex].content,
                                    path
                                );
                                return { ...prev, bio_sections: sectionsCopy };
                            });
                        }}
                    >
                        Delete
                    </button>
                </div>
            );
        }

        // If array, render each item + reorder controls
        if (Array.isArray(content)) {
            return (
                <div className="nested-array" key={path.join("_")}>
                    {content.map((item, idx) => (
                        <div className="array-item" key={idx}>
                            <div className="array-item-controls">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => moveArrayItemUp(sectionIndex, path, idx)}
                                >
                                    Up
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => moveArrayItemDown(sectionIndex, path, idx)}
                                >
                                    Down
                                </button>
                            </div>
                            {renderEditableContent(sectionIndex, item, [...path, idx])}
                        </div>
                    ))}
                    <button
                        className="btn btn-secondary add-button"
                        onClick={() => {
                            // Add an empty string item by default
                            setBioData((prev) => {
                                const sectionsCopy = [...prev.bio_sections];
                                const newArray = [...content, ""];
                                sectionsCopy[sectionIndex].content = updateNestedValue(
                                    sectionsCopy[sectionIndex].content,
                                    path,
                                    newArray
                                );
                                return { ...prev, bio_sections: sectionsCopy };
                            });
                        }}
                    >
                        + Add Item
                    </button>
                </div>
            );
        }

        // If object, recursively render key-value pairs
        if (typeof content === "object" && content !== null) {
            return (
                <div className="nested-object" key={path.join("_")}>
                    {Object.entries(content).map(([key, val]) => (
                        <div className="object-field" key={key}>
                            <label className="object-label">{key}:</label>
                            {renderEditableContent(sectionIndex, val, [...path, key])}
                        </div>
                    ))}
                    <button
                        className="btn btn-secondary add-button"
                        onClick={() => {
                            const newKey = prompt("Enter new field name:");
                            if (newKey) {
                                setBioData((prev) => {
                                    const sectionsCopy = [...prev.bio_sections];
                                    // Add a new empty string field
                                    sectionsCopy[sectionIndex].content = updateNestedValue(
                                        sectionsCopy[sectionIndex].content,
                                        path.concat([newKey]),
                                        ""
                                    );
                                    return { ...prev, bio_sections: sectionsCopy };
                                });
                            }
                        }}
                    >
                        + Add Field
                    </button>
                </div>
            );
        }

        // If null or undefined, return nothing
        return null;
    };

    /** ---------------------------------------------
     *    Save Handler
     * --------------------------------------------- */
    const handleSave = () => {
        alert("Bio data saved to console!");
        console.log("Saved bio data:", bioData);
        // Replace this with an actual API call or GitHub upload if needed
    };

    return (
        <div className="bio-editor-pro">
            {/* Title */}
            <div className="bio-header">
                <input
                    type="text"
                    className="bio-title-editable"
                    value={bioData.title}
                    onChange={handleTitleChange}
                />
            </div>

            {/* Carousel */}
            <div className="carousel-container">
                <MyCarousel images={bioData.carousel_images} />
                <div className="carousel-edit-list">
                    {bioData.carousel_images.map((img, i) => (
                        <div key={i} className="carousel-edit-item">
                            <input
                                type="text"
                                value={img}
                                onChange={(e) => handleImageChange(i, e.target.value)}
                                className="input-field"
                            />
                            <button
                                onClick={() => handleDeleteImage(i)}
                                className="btn btn-danger"
                            >
                                Delete
                            </button>
                        </div>
                    ))}
                    <button onClick={handleAddImage} className="btn btn-primary">
                        + Add Image
                    </button>
                </div>
            </div>

            {/* Sections */}
            <div className="sections-container">
                {bioData.bio_sections.map((section, sectionIndex) => (
                    <div className="bio-section" key={sectionIndex}>
                        <div className="section-header">
                            <input
                                type="text"
                                value={section.section_title}
                                onChange={(e) => handleSectionTitleChange(sectionIndex, e.target.value)}
                                className="section-title-editable"
                            />
                            <div className="section-controls">
                                <button
                                    onClick={() => moveSectionUp(sectionIndex)}
                                    className="btn btn-secondary"
                                >
                                    Up
                                </button>
                                <button
                                    onClick={() => moveSectionDown(sectionIndex)}
                                    className="btn btn-secondary"
                                >
                                    Down
                                </button>
                                <button
                                    onClick={() => handleDeleteSection(sectionIndex)}
                                    className="btn btn-danger"
                                >
                                    Delete Section
                                </button>
                            </div>
                        </div>
                        {/* Recursively render the content (arrays, objects, strings) */}
                        <div className="section-content">
                            {renderEditableContent(sectionIndex, section.content)}
                        </div>
                    </div>
                ))}
                <button onClick={handleAddSection} className="btn btn-primary add-section-btn">
                    + Add New Section
                </button>
            </div>

            {/* Save Button */}
            <h1>RESULT</h1>
            <Bio bioData={bioData} />
        </div>
    );
};

export default BioEditorPro;
